<script lang="ts" setup>
import { ref, computed } from 'vue'

import type { Props } from './types'

import AppSlider from '@/components/AppSlider/index.vue'
import AppIconsBar from '@/components/App/IconsBarV2/index.vue'

const props = defineProps<Props>()

const { t } = useI18n()
const emit = defineEmits(['selectedTypologyCode'])

const selectedTypologyCode = ref(props.currentTypologyCode)
const shortedTypologies = computed(() => [...props.typologies].sort((a, b) => a.min_area - b.min_area))

const scrollToTab = (typologyCode: string) => {
  if (selectedTypologyCode.value === typologyCode) return 'ALREADY_SELECTED'

  const tab = document.getElementById(`typology-tab-${typologyCode}`)

  if (tab) {
    tab.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
  }
}

const selectTypology = (typologyCode: string) => {
  scrollToTab(typologyCode)

  selectedTypologyCode.value = typologyCode
  emit('selectedTypologyCode', typologyCode)
}
</script>

<script lang="ts">
export default {
  name: 'TypologySlider'
}
</script>

<template>
  <div class="typology-container w-full bg-white">
    <div class="typology-slider w-full">
      <AppSlider
        id="quotation-plan-typology-tabs-slider"
        show-buttons
        lateral-paddings="hidden"
        class="border-b-2 border-carbon-light-50"
        :data-attributes="dataAttributes"
        navigation-button-size="sm">
        <ul class="typology-container__tabs-holder">
          <li
            v-for="(typology, index) in shortedTypologies"
            :id="`typology-tab-${typology.name}`"
            :key="`typology-tab-${typology.name}`"
            v-lh-id="{...dataAttributes.dataLh, A: 'seleccionar', C: typology.name, CTI: typology.min_unit_code, I: index}"
            class="typology-container__tab text-24 font-semibold"
            :class="{ 'selected-typology': selectedTypologyCode === typology.name }"
            @click="selectTypology(typology.name)">
            <p
              class="text-24 font-semibold pointer-events-none">
              {{ t('squareMeter', { area: typology.min_area}) }}
            </p>

            <AppIconsBar
              v-if="props.listingType !== 'Lote'"
              class="mt-16 pointer-events-none"
              :data="{
                bedroom: typology.bedroom,
                bathroom: typology.bathroom,
                parkingLots: typology.parking_numbers
              }"
              :hidden-elements="[]" />
          </li>
        </ul>
      </AppSlider>
    </div>
  </div>
</template>

<style lang="scss">
.lh-roomie-v2 {
  .typology-container {
    &__tabs-holder {
      @apply flex gap-8 lg:gap-16;
    }

    &__tab {
      @apply px-24 py-24 cursor-pointer hover:bg-carbon-light-50;

      border: 0;
      border-radius: 24px;
      background: #F2F4F8;

      @media(min-width: theme('screens.md')) {
        @apply px-32 py-32;
      }

      &.selected-typology {
        background: #2DE187;
      }

      p {
        color: #004132;
      }
    }

    & .app-slider {
      @apply border-b-0;

      &__button {
        @apply absolute;

        &--left {
          @apply left-0;
        }

        &--right {
          @apply right-0;
        }
      }
    }
  }
}
</style>

<i18n src="./i18n.json" lang="json" />
